import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Briefcase, Hand } from "lucide-react";
import { US_STATES } from "@/lib/constants";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";

const TYPES = ["Full-time", "Part-time", "PRN", "Travel", "New grad program"];
const DISCIPLINES = ["PT", "PTA", "OT", "OTA", "SLP", "SLPA"];
const fmt = fmtDate;

export function JobOpenings() {
  const [rows, setRows] = useState([]);
  const [interests, setInterests] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => {
    api.get("/job-openings?manage=1").then(({ data }) => setRows(data)).catch(() => {});
    api.get("/job-interests").then(({ data }) => setInterests(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const remove = async (j) => {
    try { await api.delete(`/job-openings/${j.id}`); toast.success("Opening removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <Card className="mb-6" title="Job openings students can raise their hand for" data-testid="job-openings-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <p className="text-sm text-zinc-500">
            These appear on the dashboard of every student whose discipline matches. When they raise their hand
            we flag it on their pipeline record, notify the recruiters and email the contact below.
          </p>
          <Button onClick={() => setEditing({ employment_type: "Full-time", disciplines: [], active: true })} className="bg-cc-blue text-white shrink-0" data-testid="new-job-btn">
            <Plus className="w-4 h-4 mr-1.5" />New opening
          </Button>
        </div>
        {rows.length === 0 ? <EmptyState text="No openings posted yet." /> : (
          <div className="space-y-2">
            {rows.map((j) => (
              <div key={j.id} className="flex flex-wrap items-center gap-3 border rounded-lg px-3.5 py-2.5" data-testid={`job-row-${j.id}`}>
                <Briefcase className="w-4 h-4 text-cc-blue shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-900 truncate">{j.title}</p>
                  <p className="text-xs text-zinc-500">
                    {[j.facility_name, j.city, j.state].filter(Boolean).join(" · ") || "Multiple locations"} · {j.employment_type}
                    {(j.disciplines || []).length > 0 ? ` · ${j.disciplines.join(", ")}` : " · all disciplines"}
                  </p>
                </div>
                <span className="text-xs text-zinc-500 flex items-center gap-1" data-testid={`job-interest-count-${j.id}`}>
                  <Hand className="w-3.5 h-3.5 text-cc-orange" />{j.interest_count || 0}
                </span>
                {j.active === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">Closed</span>}
                <button onClick={() => setEditing(j)} className="text-zinc-400 hover:text-cc-blue" data-testid={`job-edit-${j.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(j)} className="text-zinc-400 hover:text-red-600" data-testid={`job-delete-${j.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="mb-6" title={`Students who raised their hand (${interests.length})`} data-testid="job-interests-card">
        {interests.length === 0 ? <EmptyState text="Nobody yet — openings show on student dashboards as soon as you post them." /> : (
          <div className="space-y-2">
            {interests.map((r) => (
              <div key={r.id} className="border rounded-lg px-3.5 py-2.5" data-testid={`job-interest-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-zinc-900">{r.student_name}</p>
                  <span className="text-xs text-zinc-400">{r.discipline}</span>
                  <span className="text-xs text-zinc-400">· raised {fmt(r.created_at)}</span>
                  {r.student?.graduation_date && <span className="text-xs text-zinc-400">· graduates {fmt(r.student.graduation_date)}</span>}
                </div>
                <p className="text-xs text-cc-blue mt-0.5">{r.job_title}</p>
                {r.note && <p className="text-xs text-zinc-600 mt-1 italic">“{r.note}”</p>}
                {(r.student?.personal_email || r.student?.school_email) && (
                  <a href={`mailto:${r.student.personal_email || r.student.school_email}`} className="text-xs text-zinc-500 hover:text-cc-blue">
                    {r.student.personal_email || r.student.school_email}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {editing && <JobDialog job={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function JobDialog({ job, onClose, onSaved }) {
  const [form, setForm] = useState({ disciplines: [], ...job });
  const [saving, setSaving] = useState(false);
  const toggle = (d) => setForm({
    ...form,
    disciplines: (form.disciplines || []).includes(d)
      ? form.disciplines.filter((x) => x !== d)
      : [...(form.disciplines || []), d],
  });
  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.patch(`/job-openings/${form.id}`, form);
      else await api.post("/job-openings", form);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto cc-scroll" data-testid="job-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Edit opening" : "New opening"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Title">
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Physical Therapist — Miami" data-testid="job-title" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Facility / team">
              <Input value={form.facility_name || ""} onChange={(e) => setForm({ ...form, facility_name: e.target.value })} data-testid="job-facility" />
            </Field>
            <Field label="Employment type">
              <Select value={form.employment_type || "Full-time"} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                <SelectTrigger data-testid="job-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="City">
              <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="job-city" />
            </Field>
            <Field label="State">
              <Select value={form.state || ""} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger data-testid="job-state"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{US_STATES.map(([code, name]) => <SelectItem key={code} value={code}>{code} — {name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Disciplines (leave empty to show every student)">
            <div className="flex flex-wrap gap-3" data-testid="job-disciplines">
              {DISCIPLINES.map((d) => (
                <label key={d} className="flex items-center gap-1.5 text-sm text-zinc-600">
                  <Checkbox checked={(form.disciplines || []).includes(d)} onCheckedChange={() => toggle(d)} data-testid={`job-disc-${d}`} />{d}
                </label>
              ))}
            </div>
          </Field>
          <Field label="What students should know">
            <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="job-description" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Who gets the interest email">
              <Input value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="recruiting@therapymgmt.com" data-testid="job-contact-email" />
            </Field>
            <Field label="Apply online link (optional)">
              <Input value={form.apply_url || ""} onChange={(e) => setForm({ ...form, apply_url: e.target.value })} data-testid="job-apply-url" />
            </Field>
          </div>
          <div className="flex items-center gap-2.5">
            <Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="job-active" />
            <span className="text-sm text-zinc-600">{form.active !== false ? "Showing on student dashboards" : "Closed — hidden from students"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="job-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !(form.title || "").trim()} className="bg-cc-blue text-white" data-testid="job-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
